import { useCallback, useEffect, useRef } from "react";
import Script from "next/script";
import dynamic from "next/dynamic";
import { useForm, Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "@/hooks/useTranslation";

import CartStatus from "@/components/checkout/cart-status";
import CartView from "@/components/checkout/cart-view";
import CheckoutContext from "@/components/checkout/checkout-context";
import GuestCheckout from "@/components/checkout/guest-checkout";
import Header from "@/components/checkout/header";
import useJwt from "@/components/checkout/hooks/useJwt";
import useLanguageSync from "@/components/checkout/hooks/useLanguageSync";
import { useSetDefaultCheckoutValues } from "@/components/checkout/hooks/useSetDefaultCheckoutValues";
import LoggedInCheckout from "./logged-in-checkout";
import { CheckoutMetadata } from "./hooks/useCheckoutMetadata";
import TranslationProvider from "../providers/TranslationProvider";
import type { CheckoutFormData } from "./checkout.types";
import {
  fetchCountriesAndLanguages,
  AddressField,
} from "@/networking/addresses.api";
import { DEFAULT_ADDRESS_FIELDS } from "@/lib/country-config";
import { fetchCart } from "@/networking/carts.api";
import { inputCheckoutSchema } from "@/networking/checkout.api";
import { useChainedScrolling } from "./hooks/useChainedScrolling";
import { useAddressFields } from "@/hooks/useAddressFields";
import { AFFIRM_LOCALE_CONFIG } from "@/lib/affirm-locale-config";
import { useKount } from "@/hooks/useKount";
import {
  resolveKountSdkEnvironment,
  resolveKountClientId,
} from "@/lib/kount-env";
import useBrandColors from "./hooks/useBrandColors";
const PaypalProvider = dynamic(
  () =>
    import("./paypal-provider").then((mod) => ({
      default: mod.PaypalProvider,
    })),
  { ssr: false },
);
const BraintreeProvider = dynamic(
  () =>
    import("./braintree-provider").then((mod) => ({
      default: mod.BraintreeProvider,
    })),
  { ssr: false },
);
import { useCartMutations } from "./hooks/useCartMutations";
import { LoadingOverlayProvider } from "../providers/CheckoutLoading";
import { useRealtimeCartSync } from "@/hooks/useRealtimeCartSync";

declare global {
  interface AffirmCheckoutOptions {
    merchant: {
      user_confirmation_url: string;
      user_cancel_url: string;
      public_api_key?: string;
      user_confirmation_url_action?: string;
      name?: string;
    };
    shipping: Record<string, unknown>;
    billing?: Record<string, unknown>;
    items: Array<Record<string, unknown>>;
    metadata?: Record<string, unknown>;
    order_id?: string;
    currency: string;
    financing_program?: string;
    shipping_amount?: number;
    tax_amount?: number;
    total: number;
    mode?: string;
  }
  interface AffirmCheckoutCallbacks {
    onFail?: (error: { checkout_token: string; reason: string }) => void;
    onSuccess?: (checkout: { created: string; checkout_token: string }) => void;
    onValidationError?: (error: unknown) => void;
  }
  interface AffirmCheckout {
    (options: AffirmCheckoutOptions): void;
    open: (callbacks: AffirmCheckoutCallbacks) => void;
  }
  interface Window {
    affirm: {
      checkout: AffirmCheckout;
      ui: { ready: (callback: () => void) => void };
    };
    Klarna: {
      Payments: {
        init: (options: { client_token: string }) => Promise<void>;
        load: (
          options: { container: string; payment_method_category: string },
          data: Record<string, unknown>,
          callback: (result: { show_form: boolean }) => void,
        ) => void;
        authorize: (
          options: { payment_method_category: string },
          data: Record<string, unknown>,
          callback: (result: {
            approved: boolean;
            authorization_token?: string;
            error?: string;
          }) => void,
        ) => void;
      };
    };
    ApplePaySession?: typeof ApplePaySession;
  }
}

// Flatten an RHF formState.errors object to dotted leaf paths
// (e.g. "customer.shipping_address.first_name"). RHF's nested error shape
// stores a FieldError ({ type: string, message?: string, ref? }) at each
// leaf and bare nested objects at intermediate nodes. We discriminate by
// checking that `type` is a string — the FieldError contract guarantees
// it. Just checking `"type" in obj` would misfire if a future schema
// adds a field literally named `type`, where the value would be a nested
// FieldError object rather than a string.
function collectErrorPaths(
  errors: Record<string, unknown>,
  prefix = "",
): string[] {
  const paths: string[] = [];
  for (const key of Object.keys(errors)) {
    const value = errors[key];
    if (!value || typeof value !== "object") continue;
    const path = prefix ? `${prefix}.${key}` : key;
    const obj = value as Record<string, unknown>;
    if (typeof obj.type === "string") {
      paths.push(path);
    } else {
      paths.push(...collectErrorPaths(obj, path));
    }
  }
  return paths;
}

export function Checkout({
  cartToken,
  isMobile,
  paymentMethodsIncludes,
  inFrame,
}: {
  cartToken: string;
  isMobile: boolean;
  paymentMethodsIncludes?: string;
  inFrame?: boolean;
}) {
  const { t, language } = useTranslation();
  const queryClient = useQueryClient();
  const { recalculateCartMutation, updateLanguage } = useCartMutations(
    cartToken,
    paymentMethodsIncludes ?? null,
  );

  const { rightSideRef, leftSideRef } = useChainedScrolling();
  const hasRecalculatedCartRef = useRef(false);

  useRealtimeCartSync(cartToken, true, paymentMethodsIncludes);

  // Track pending mutations for disabling interactions
  const isSubscriptionPending =
    useIsMutating({
      mutationKey: ["update-item-subscription", cartToken],
    }) > 0;
  const isQuantityPending =
    useIsMutating({
      mutationKey: ["update-item-quantity", cartToken],
    }) > 0;
  const isContactInfoPending =
    useIsMutating({
      mutationKey: ["update-contact-info", cartToken],
    }) > 0;

  const query = useQuery({
    queryKey: ["cart", cartToken, paymentMethodsIncludes],
    queryFn: () => fetchCart(cartToken, paymentMethodsIncludes),
    enabled: !!cartToken,
  });

  // Scope the stored JWT to this cart's shop: a JWT minted while shopping a
  // different fluid_shop is treated as logged out (useJwt clears it), since
  // the backend rejects cross-company tokens anyway. isLoggedIn additionally
  // waits for the shop to be known so the logged-in subtree never mounts —
  // and never fires authenticated queries — on a not-yet-verified cookie JWT
  // while the cart is still loading.
  const expectedShop = query.data?.cart?.company?.subdomain;
  const { jwt, decodedJwt } = useJwt(expectedShop);
  const isLoggedIn = Boolean(expectedShop && jwt && decodedJwt);

  // Initialize Kount with per-merchant env from cart response.
  // resolveKountSdkEnvironment returns undefined while loading or errored,
  // so no DDC session is started before we know the merchant's environment.
  const kountSessionId = useKount(
    resolveKountSdkEnvironment(query),
    resolveKountClientId(query),
  );

  useEffect(() => {
    if (cartToken && !hasRecalculatedCartRef.current) {
      recalculateCartMutation.mutate();
      hasRecalculatedCartRef.current = true;
    }
  }, [cartToken, recalculateCartMutation]);

  const paymentMethodAutoAssignable =
    query.data?.cart?.payment_method_auto_assignable ?? false;

  const requiresPaymentMethod =
    query.data?.cart?.requires_payment_method ?? true;

  const requiredAgreements =
    query.data?.cart?.agreements?.length &&
    query.data?.cart?.agreements.some((agreement) => agreement.required);

  const { addressFields } = useAddressFields(
    query.data?.cart?.country?.iso ?? "US",
  );

  // keep current translation function in ref for dynamic resolver
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  // dynamic resolver that uses current translations on every validation
  const dynamicResolver: Resolver<CheckoutFormData> = useCallback(
    async (values, context, options) => {
      const currentT = tRef.current;

      // look up billing address fields from cache for the billing country
      const billingCountryCode =
        values.customer?.payment_method?.billing_address?.country_code;
      let billingAddressFields: AddressField[] | undefined;
      if (billingCountryCode) {
        const cachedData = queryClient.getQueryData<{
          address_fields?: { fields?: AddressField[] };
        }>(["addressFields", billingCountryCode, language]);
        billingAddressFields =
          cachedData?.address_fields?.fields ?? DEFAULT_ADDRESS_FIELDS;
      }

      const schema = inputCheckoutSchema(
        query.data?.cart?.company?.require_phone ?? false,
        addressFields,
        !query.data?.cart?.digital_only,
        requiresPaymentMethod && !paymentMethodAutoAssignable,
        !!requiredAgreements,
        {
          invalidEmail: currentT("invalid_email"),
          phoneRequired: currentT("phone_number_is_required"),
          shippingMethodRequired: currentT("shipping_method_is_required"),
          paymentMethod: {
            cardNumberRequired: currentT("card_number_is_required"),
            cvcMinDigits: currentT("cvc_minimum_digits"),
            expirationDateRequired: currentT("expiration_date_is_required"),
            firstNameRequired: currentT("first_name_is_required"),
            lastNameRequired: currentT("last_name_is_required"),
            billingAddress: {
              nameRequired: currentT("name_is_required"),
              addressLine1Required: currentT("address_line_1_is_required"),
              cityRequired: currentT("city_is_required"),
              stateRequired: currentT("state_is_required"),
              postalCodeRequired: currentT("postal_code_is_required"),
              countryRequired: currentT("country_is_required"),
              phoneRequired: currentT("address_phone_number_is_required"),
            },
          },
          shippingAddress: {
            nameRequired: currentT("name_is_required"),
            addressLine1Required: currentT("address_line_1_is_required"),
            cityRequired: currentT("city_is_required"),
            stateRequired: currentT("state_is_required"),
            postalCodeRequired: currentT("postal_code_is_required"),
            countryRequired: currentT("country_is_required"),
            phoneRequired: currentT("address_phone_number_is_required"),
          },
        },
        query.data?.cart?.digital_only ?? false,
        billingAddressFields,
      );
      return zodResolver(schema as any)(values, context, options);
    },
    [
      query.data?.cart?.company?.require_phone,
      addressFields,
      query.data?.cart?.digital_only,
      paymentMethodAutoAssignable,
      requiresPaymentMethod,
      requiredAgreements,
      queryClient,
      language,
    ],
  );

  const checkoutForm = useForm<CheckoutFormData>({
    resolver: dynamicResolver,
    defaultValues: {
      customer: {
        contact_info: {
          email: "",
          subscribe_to_news: true,
          phone: "",
        },
        shipping_address: {
          name: "",
          address1: "",
          address2: "",
          city: "",
          state: "",
          postal_code: "",
          country: "",
          country_code: "",
        },
        shipping_method: {
          id: "",
        },
        payment_method: {
          type: "credit_card",
          use_shipping_address_for_billing: true,
          card_number: "",
          expiration_date: "",
          cvc: "",
          first_name: "",
          last_name: "",
          billing_address: {
            name: "",
            address1: "",
            address2: "",
            city: "",
            state: "",
            postal_code: "",
            country: "",
            country_code: "",
          },
        },
        remember_me: true,
        agreements_accepted: true,
      },
    },
  });

  useSetDefaultCheckoutValues(checkoutForm, query);
  useLanguageSync(query.data?.cart?.language_iso);
  useBrandColors(
    query.data?.cart?.company?.checkout_primary_button_color,
    query.data?.cart?.company?.checkout_primary_text_color,
  );

  // Re-translate validation errors when the user switches language.
  // formState.errors is nested (e.g. errors.customer.shipping_address.first_name),
  // and Object.keys returns only top-level keys like ["customer"], so calling
  // trigger() with those would re-validate entire subtrees and surface
  // errors for required fields the user has since cleared. We flatten to
  // leaf paths first and trigger only those — fields without a current
  // visible error stay untouched.
  const previousLanguageRef = useRef(language);

  useEffect(() => {
    if (previousLanguageRef.current !== language) {
      const errorPaths = collectErrorPaths(checkoutForm.formState.errors);
      if (errorPaths.length > 0) {
        checkoutForm.clearErrors();
        checkoutForm.trigger(
          errorPaths as Parameters<typeof checkoutForm.trigger>[0],
        );
      }
    }
    previousLanguageRef.current = language;
  }, [language, checkoutForm]);

  const { data: countriesAndLanguages } = useQuery({
    queryKey: ["countries-and-languages", cartToken, language],
    queryFn: () => fetchCountriesAndLanguages(cartToken, language),
    select: (data) => {
      if (data && Array.isArray(data.countries)) {
        const sortedCountries = [...data.countries].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        return { ...data, countries: sortedCountries };
      }
      return data;
    },
  });

  const companyLogoUrl = query.data?.cart?.company?.logo_url ?? "";
  const shopUrl = query.data?.meta?.shop_url ?? "";
  const items = query.data?.cart?.items ?? [];
  const processed = query.data?.cart?.processed ?? false;
  const affirmPublicKey = query.data?.cart?.available_payment_methods?.find(
    (method) => method.type === "affirm",
  )?.public_api_key;
  const isSandbox = query.data?.cart?.available_payment_methods?.find(
    (method) => method.type === "affirm",
  )?.sandbox;

  const paypalMethod = query.data?.cart?.available_payment_methods?.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- cart API response type
    (m: any) => m.type === "paypal",
  );
  const sdkType = paypalMethod?.sdk_type || "ppcp";
  const braintreePaymentAccountId = paypalMethod?.braintree_payment_account_id;

  if (items.length === 0 && query.data) {
    return (
      <CartStatus
        shopUrl={shopUrl}
        title={t("your_cart_is_empty")}
        description={t("looks_like_you_havent_added_any_items")}
        showButton={!isMobile && !inFrame}
      />
    );
  }

  if (processed) {
    return (
      <CartStatus
        shopUrl={shopUrl}
        title={t("order_has_been_processed")}
        description={t("you_can_close_this_window")}
        showButton={!isMobile && !inFrame}
      />
    );
  }

  const hasStatus410 = (err: unknown): boolean =>
    typeof err === "object" &&
    err !== null &&
    "status" in err &&
    (err as { status: unknown }).status === 410;

  const isCartGone =
    hasStatus410(query.error) || hasStatus410(recalculateCartMutation.error);

  if (isCartGone) {
    return (
      <CartStatus
        shopUrl={shopUrl}
        title={t("cart_unavailable")}
        description={t("cart_no_longer_available")}
        showButton={!isMobile && !inFrame}
      />
    );
  }

  if (query.isError) {
    return (
      <CartStatus
        shopUrl={shopUrl}
        title={t("404")}
        description={t("Page not found")}
        showButton={!isMobile && !inFrame}
      />
    );
  }

  return (
    <TranslationProvider>
      <LoadingOverlayProvider>
        {!query.isLoading && (
          <CheckoutMetadata
            companyName={query.data?.cart?.company?.name}
            companyFaviconUrl={query.data?.cart?.company?.favicon_url || ""}
          />
        )}
        {affirmPublicKey &&
          (() => {
            const cartCountry = query.data?.cart?.country?.iso;
            const config = cartCountry
              ? AFFIRM_LOCALE_CONFIG[
                  cartCountry as keyof typeof AFFIRM_LOCALE_CONFIG
                ]
              : null;

            if (!config) return null;

            let locale: string = config.default_locale;
            const userLanguage = query.data?.cart?.language_iso;

            if (userLanguage) {
              const preferredLocale = config.locales.find((l) =>
                l.toLowerCase().includes(userLanguage.toLowerCase()),
              );
              if (preferredLocale) locale = preferredLocale;
            }

            // Browser language fallback
            if (typeof navigator !== "undefined" && navigator.language) {
              const browserLang = navigator.language.toLowerCase();
              const exactMatch = config.locales.find((l) => {
                const localeLang = l.toLowerCase().replace("_", "-");
                const firstLangPart = localeLang.split("-")[0];
                return (
                  browserLang === localeLang ||
                  (firstLangPart && browserLang.startsWith(firstLangPart))
                );
              });
              if (exactMatch !== undefined) {
                locale = exactMatch;
              }
            }

            return (
              <>
                <Script id="affirm-config" strategy="lazyOnload">
                  {`window._affirm_config = {
          public_api_key: "${affirmPublicKey}",
          script: "${
            isSandbox
              ? "https://cdn1-sandbox.affirm.com/js/v2/affirm.js"
              : "https://cdn1.affirm.com/js/v2/affirm.js"
          }",
          locale: "${locale}",
          country_code: "${config.country_code}"
        };`}
                </Script>
                <Script
                  id="affirm-js"
                  src={
                    isSandbox
                      ? "https://cdn1-sandbox.affirm.com/js/v2/affirm.js"
                      : "https://cdn1.affirm.com/js/v2/affirm.js"
                  }
                  strategy="lazyOnload"
                />
              </>
            );
          })()}
        {/* Only load Klarna SDK if Klarna is available as a payment method */}
        {query.data?.cart?.available_payment_methods?.some(
          (m) => m.type === "klarna",
        ) && (
          <Script
            id="klarna-js"
            src="https://x.klarnacdn.net/kp/lib/v1/api.js"
            strategy="lazyOnload"
          />
        )}
        {/* Apple's Apple Pay JS SDK wraps window.PaymentRequest on Chromium so
            `https://apple.com/apple-pay` resolves to the QR-code flow instead of
            failing the native manifest check. Load it whenever any cart payment
            method can drive Apple Pay — CreditCard (VGS) or decrypted PayPal. */}
        {query.data?.cart?.available_payment_methods?.some(
          (m) =>
            (m.type === "credit_card" && m.apple_pay === true) ||
            (m.type === "paypal" && m.decrypted_apple_pay_via_paypal === true),
        ) && (
          <Script
            id="applepay-js"
            src="https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js"
            strategy="lazyOnload"
          />
        )}
        {(() => {
          const checkoutContent = (
            <CheckoutContext>
              {!inFrame && !isMobile && (
                <Header
                  isLoading={query.isLoading}
                  companyLogoUrl={companyLogoUrl}
                  shopUrl={shopUrl}
                  handleLanguageChange={(language) => {
                    updateLanguage.mutate({ language_iso: language });
                  }}
                  companyName={query.data?.cart?.company?.name ?? ""}
                />
              )}

              <div className="flex flex-col lg:grid lg:grid-cols-8">
                <CartView
                  cartToken={cartToken}
                  query={query}
                  paymentMethodsIncludes={paymentMethodsIncludes ?? null}
                  scrollRef={leftSideRef}
                  inFrame={inFrame}
                  isSubscriptionPending={isSubscriptionPending}
                  isQuantityPending={isQuantityPending}
                  isContactInfoPending={isContactInfoPending}
                />
                {isLoggedIn ? (
                  <LoggedInCheckout
                    query={query}
                    checkoutForm={checkoutForm}
                    cartToken={cartToken}
                    jwt={jwt}
                    decodedJwt={decodedJwt}
                    affirmPublicKey={affirmPublicKey}
                    paymentMethodsIncludes={paymentMethodsIncludes ?? null}
                    scrollRef={rightSideRef}
                    kountSessionId={kountSessionId}
                    sdkType={sdkType}
                    braintreePaymentAccountId={braintreePaymentAccountId}
                    countries={countriesAndLanguages?.countries ?? []}
                  />
                ) : (
                  <GuestCheckout
                    checkoutForm={checkoutForm}
                    cartToken={cartToken}
                    query={query}
                    countries={countriesAndLanguages?.countries ?? []}
                    affirmPublicKey={affirmPublicKey}
                    paymentMethodsIncludes={paymentMethodsIncludes ?? null}
                    scrollRef={rightSideRef}
                    kountSessionId={kountSessionId}
                    sdkType={sdkType}
                    braintreePaymentAccountId={braintreePaymentAccountId}
                  />
                )}
              </div>
            </CheckoutContext>
          );

          return sdkType === "braintree" && braintreePaymentAccountId ? (
            <BraintreeProvider
              cartToken={cartToken}
              paymentAccountId={braintreePaymentAccountId}
              currencyCode={query.data?.cart?.currency_code ?? ""}
            >
              {checkoutContent}
            </BraintreeProvider>
          ) : (
            <PaypalProvider
              cartToken={cartToken}
              paymentMethodsIncludes={paymentMethodsIncludes}
            >
              {checkoutContent}
            </PaypalProvider>
          );
        })()}
      </LoadingOverlayProvider>
    </TranslationProvider>
  );
}
